import { useMemo } from 'react';
import {
  Responsive,
  WidthProvider,
  type Layout,
} from 'react-grid-layout';
import {
  GRID_COLS,
  GRID_ROWS,
  WIDGET_MAX_H,
  WIDGET_MAX_W,
  WIDGET_MIN_H,
  WIDGET_MIN_W,
  type DashboardPageId,
  type WidgetLayout,
} from '@proadv/types';

import { WidgetCard } from './widget-card';
import { getWidgetMeta } from './widget-registry';

const ResponsiveGrid = WidthProvider(Responsive);

const ROW_HEIGHT = 200;
const MARGIN: [number, number] = [16, 16];

interface DashboardGridProps {
  pageId: DashboardPageId;
  layout: WidgetLayout[];
  mode: 'view' | 'edit';
  onLayoutChange?: (layout: WidgetLayout[]) => void;
  onRemove?: (widgetId: string) => void;
}

/**
 * Converte o array de WidgetLayout para o formato esperado pelo
 * react-grid-layout (Layout[]).
 */
function toRglLayout(layout: WidgetLayout[]): Layout[] {
  return layout.map((w) => ({
    i: w.widgetId,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    minW: WIDGET_MIN_W,
    maxW: WIDGET_MAX_W,
    minH: WIDGET_MIN_H,
    maxH: WIDGET_MAX_H,
  }));
}

/**
 * Inverte: layout do RGL -> WidgetLayout[] tipado.
 */
function fromRglLayout(rgl: Layout[]): WidgetLayout[] {
  return rgl.map((item) => ({
    widgetId: item.i as WidgetLayout['widgetId'],
    x: item.x,
    y: item.y,
    w: Math.min(Math.max(item.w, WIDGET_MIN_W), WIDGET_MAX_W),
    h: Math.min(Math.max(item.h, WIDGET_MIN_H), WIDGET_MAX_H),
  }));
}

export function DashboardGrid({
  pageId,
  layout,
  mode,
  onLayoutChange,
  onRemove,
}: DashboardGridProps) {
  const rglLayout = useMemo(() => toRglLayout(layout), [layout]);

  return (
    <ResponsiveGrid
      className="layout"
        layouts={{ lg: rglLayout, md: rglLayout, sm: rglLayout }}
        breakpoints={{ lg: 1024, md: 768, sm: 0 }}
        cols={{ lg: GRID_COLS, md: 2, sm: 1 }}
        maxRows={GRID_ROWS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        containerPadding={[0, 0]}
        isDraggable={mode === 'edit'}
        isResizable={mode === 'edit'}
        compactType="vertical"
        preventCollision={false}
        onLayoutChange={(next) => {
        if (!onLayoutChange) return;
        onLayoutChange(fromRglLayout(next));
      }}
    >
      {layout.map((w) => {
        const meta = getWidgetMeta(pageId, w.widgetId);
        if (!meta) return null;
        return (
          <div key={w.widgetId}>
            <WidgetCard
              widgetId={w.widgetId}
              mode={mode}
              Component={meta.Component}
              onRemove={onRemove}
            />
          </div>
        );
      })}
    </ResponsiveGrid>
  );
}
